import { useState } from "react"
import type { KnowledgeCardData } from "../types"
import { VideoCard } from "./VideoCard"

/** Lightweight markdown-ish renderer for KB article bodies. The KB is
 *  markdown but the widget deliberately has no markdown dependency — we
 *  format the structures that actually appear in our docs (headers,
 *  bullets, bold) into styled blocks and strip the rest. */
function ArticleBody({ content }: { content: string }) {
  const blocks = content.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean)
  const strip = (t: string) => t.replace(/\*\*([^*]+)\*\*/g, "$1").replace(/`([^`]+)`/g, "$1")
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
      {blocks.map((block, i) => {
        const h = block.match(/^(#{1,4})\s+(.*)$/m)
        if (h && block.startsWith(h[1])) {
          const rest = block.slice(h[0].length).trim()
          return (
            <div key={i}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.95)", margin: "4px 0 2px" }}>
                {strip(h[2])}
              </div>
              {rest && (
                <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.78)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                  {strip(rest)}
                </div>
              )}
            </div>
          )
        }
        if (/^[-*•]\s/m.test(block)) {
          const items = block.split("\n").map((l) => l.replace(/^[-*•]\s+/, "").trim()).filter(Boolean)
          return (
            <ul key={i} style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 4 }}>
              {items.map((it, j) => (
                <li key={j} style={{ fontSize: 12.5, color: "rgba(255,255,255,0.78)", lineHeight: 1.5 }}>
                  {strip(it)}
                </li>
              ))}
            </ul>
          )
        }
        return (
          <div key={i} style={{ fontSize: 12.5, color: "rgba(255,255,255,0.78)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
            {strip(block)}
          </div>
        )
      })}
    </div>
  )
}

/** Knowledge surface — articles matched by search_knowledge rendered as
 *  scannable summary cards, optionally followed by a matched-video strip.
 *
 *  Most KB articles have NO public URL (internal docs), so the card expands
 *  IN PLACE to show the full body — caught 2026-06-11: "the guides that show
 *  up aren't clickable so the customer can't read the full info". Articles
 *  that do have a URL also get an external link. */
export function KnowledgeCard({ data }: { data: KnowledgeCardData }) {
  const hasArticles = data.articles.length > 0
  const hasVideos = !!data.videos && data.videos.length > 0
  const [expanded, setExpanded] = useState<number | null>(null)
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {data.query && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            opacity: 0.55,
            marginBottom: 2,
          }}
        >
          Looking up “{data.query}”
        </div>
      )}
      {hasArticles && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {data.articles.map((a, i) => {
            const isOpen = expanded === i
            const expandable = !!a.content
            const wrapperStyle = {
              padding: "12px 13px",
              borderRadius: 12,
              background: isOpen ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.05)",
              border: `1px solid ${isOpen ? "rgba(109, 195, 247, 0.35)" : "rgba(255,255,255,0.08)"}`,
              color: "inherit",
              textDecoration: "none",
              display: "block",
              width: "100%",
              textAlign: "left" as const,
              cursor: expandable || a.url ? "pointer" : "default",
              transition: "background 120ms ease, border-color 120ms ease",
              font: "inherit",
            }
            const header = (
              <>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "rgba(255,255,255,0.96)",
                    lineHeight: 1.3,
                    marginBottom: 4,
                  }}
                >
                  {a.title}
                </div>
                {a.preview && !isOpen && (
                  <div
                    style={{
                      fontSize: 12.5,
                      color: "rgba(255,255,255,0.72)",
                      lineHeight: 1.45,
                      display: "-webkit-box",
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {a.preview}
                  </div>
                )}
                {isOpen && a.content && <ArticleBody content={a.content} />}
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: 8,
                  }}
                >
                  {a.category && (
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        textTransform: "uppercase",
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: "rgba(109, 195, 247, 0.15)",
                        color: "rgba(109, 195, 247, 0.95)",
                      }}
                    >
                      {a.category}
                    </span>
                  )}
                  <span style={{ display: "inline-flex", gap: 12, alignItems: "center", marginLeft: "auto" }}>
                    {expandable && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(109, 195, 247, 0.95)" }}>
                        {isOpen ? "Show less ▴" : "Read the full guide ▾"}
                      </span>
                    )}
                    {a.url && (
                      // span-with-onClick, NOT <a> — this header renders inside
                      // a <button> when the card is expandable, and nested
                      // interactive elements are invalid HTML.
                      <span
                        role="link"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation()
                          window.open(a.url, "_blank", "noopener")
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation()
                            window.open(a.url, "_blank", "noopener")
                          }
                        }}
                        style={{ fontSize: 11, fontWeight: 500, color: "rgba(255,255,255,0.7)", cursor: "pointer" }}
                      >
                        Open page ↗
                      </span>
                    )}
                  </span>
                </div>
              </>
            )
            if (expandable) {
              return (
                <button
                  key={i}
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setExpanded(isOpen ? null : i)}
                  style={wrapperStyle}
                >
                  {header}
                </button>
              )
            }
            return a.url ? (
              <a key={i} href={a.url} target="_blank" rel="noreferrer" style={wrapperStyle}>
                {header}
              </a>
            ) : (
              <div key={i} style={wrapperStyle}>
                {header}
              </div>
            )
          })}
        </div>
      )}
      {hasVideos && (
        <div style={{ marginTop: 6 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              opacity: 0.6,
              marginBottom: 8,
            }}
          >
            Watch
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {data.videos!.map((v) => (
              <VideoCard key={v.videoId} data={v} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
